import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/screens/InteractionDetails.css';
import { api, fetchData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import { MOMENT_ICONS } from '../utils/iconMappings';
import { useKPILoading } from '../hooks/useKPILoading';
import {
  InteractionResponse, 
  TranscriptResponse, 
  EvaluationSection, 
  EvaluationQuestion as EvaluationQuestionType, 
  SwotItem,
  Evaluation,
  EvaluationResponse,
  TurnAnalysisResponse,
} from '../types/api';
import PlaybackPanel from '../components/PlaybackPanel';
import SpeechItem from '../components/SpeechItem';
import EvaluationQuestion from '../components/EvaluationQuestion';
import EvaluationPanel from '../components/EvaluationPanel';
import Layout from '../components/Layout';
import { formatDate } from '../utils/dateUtils';
import { useLayout } from '../contexts/LayoutContext';
import NavigationTabs, { NavigationTabsProps } from '../components/common/NavigationTabs';
import TranscriptPanel from '../components/TranscriptPanel';
import MomentsPanel from '../components/MomentsPanel';
import FeedbackBySection from '../components/FeedbackBySection';
import InteractionSummary from '../components/InteractionSummary';
import ScriptAdherence from '../components/ScriptAdherence';
import KPIWidget from '../components/KPIWidget';
import KPIDashboard, { KPIRow } from '../components/KPIDashboard';
import LiveAnalysisTab from '../components/LiveAnalysisTab';
import {
  getPostCallStatus,
  PostCallJob,
  PostCallStepStatus,
  runPostCall,
} from '../services/postCallClient';
import '../styles/common.css';

const InteractionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { updateHeaderProps } = useLayout();
  const [activeTab, setActiveTab] = useState('transcript');
  const [interactionData, setInteractionData] = useState<InteractionResponse | null>(null);
  const [transcriptData, setTranscriptData] = useState<TranscriptResponse | null>(null);
  const [evaluationData, setEvaluationData] = useState<Evaluation | null>(null);
  const [turnAnalysisData, setTurnAnalysisData] = useState<TurnAnalysisResponse | null>(null);
  const [turnAnalysisLoading, setTurnAnalysisLoading] = useState(false);
  const [turnAnalysisError, setTurnAnalysisError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentTimePosition, setCurrentTimePosition] = useState(0);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [highlightedTexts, setHighlightedTexts] = useState<string[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [matchPositions, setMatchPositions] = useState<{id: string, index: number}[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [leftColumnWidth, setLeftColumnWidth] = useState(65); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const [postCallJob, setPostCallJob] = useState<PostCallJob | null>(null);
  const [postCallRetrying, setPostCallRetrying] = useState(false);


  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const searchStateRef = useRef({
    currentMatchIndex: 0,
    searchText: ''
  });
  
  // Add this ref to track if we've already updated the header
  const headerUpdatedRef = useRef(false);
  const refreshedAnalysisRef = useRef<Set<string>>(new Set());
  
  // KPI loading using custom hook
  const {
    kpiDataMap,
    kpiLoading,
    kpiError,
    getKpiData
  } = useKPILoading({
    interactionIds: [id || ''],
    kpiNames: ['call_score', 'average_wpm', 'script_adherence', 'script_violations'],
    reloadKey: postCallJob?.steps.find(step => step.analysis_type === 'kpi')?.finished_at
  });

  const kpiCallScore = getKpiData(id || '', 'call_score');
  const kpiAverageWpm = getKpiData(id || '', 'average_wpm');
  const kpiAdherence = getKpiData(id || '', 'script_adherence');
  const kpiScriptViolations = getKpiData(id || '', 'script_violations');
  
  // Log KPI loading status for debugging
  useEffect(() => {
    if (kpiError) {
      console.warn('KPI loading failed in InteractionDetails:', kpiError);
    }
    if (kpiLoading) {
      console.log('KPI data loading in InteractionDetails...');
    }
  }, [kpiError, kpiLoading]);

  // Common function to refresh evaluation data
  const refreshEvaluationData = async (evaluationId: string) => {
    try {
      const evaluationUrl = `${api(PATH_SEGMENTS.EVALUATION, evaluationId)}?scores=true&all=true&insights=true&remarks=true&questions=true&form=true`;
      const evaluationResponse = await fetchData<EvaluationResponse>(evaluationUrl);
      setEvaluationData(evaluationResponse.evaluation);
    } catch (error) {
      console.error('Error refreshing evaluation data:', error);
    }
  };
  
  useEffect(() => {
    const loadData = async () => {
      if (id) {
        
        const interactionUrl = api(PATH_SEGMENTS.INTERACTION, id);
        let interaction: InteractionResponse | null = null;
        try {
          interaction = await fetchData<InteractionResponse>(interactionUrl);
          setInteractionData(interaction);
          // First load the transcript
          if (interaction?.transcript_id) {
            const transcriptUrl = api(PATH_SEGMENTS.TRANSCRIPT, interaction.transcript_id.toString());
            const transcript = await fetchData<TranscriptResponse>(transcriptUrl);
            setTranscriptData(transcript);
          }
          setError(null);
        } catch (error) {
          setError("No interaction data found");
          console.error("No interaction data found:", error);
          setInteractionData(null);
        }

        // Then load evaluation data separately if available
        if (interaction?.evaluation_id) {
          try {
            console.log("Loading evaluation data for ID:", interaction.evaluation_id);
            await refreshEvaluationData(interaction.evaluation_id.toString());
          } catch (error) {
            console.error("Failed to load evaluation data:", error);
            setEvaluationData(null);
          }
        }
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    if (!id || !postCallJob) return;

    const refreshCompletedOutputs = async () => {
      const mmtStep = postCallJob.steps.find(step => step.analysis_type === 'mmt');
      const mmtKey = `${postCallJob.id}:mmt:${mmtStep?.finished_at || ''}`;
      if (mmtStep?.status === 'succeeded' && !refreshedAnalysisRef.current.has(mmtKey)) {
        refreshedAnalysisRef.current.add(mmtKey);
        if (interactionData?.transcript_id) {
          const transcript = await fetchData<TranscriptResponse>(
            api(PATH_SEGMENTS.TRANSCRIPT, interactionData.transcript_id.toString())
          );
          setTranscriptData(transcript);
        }
      }

      const evalStep = postCallJob.steps.find(step => step.analysis_type === 'eval');
      const evalKey = `${postCallJob.id}:eval:${evalStep?.finished_at || ''}`;
      if (evalStep?.status === 'succeeded' && !refreshedAnalysisRef.current.has(evalKey)) {
        refreshedAnalysisRef.current.add(evalKey);
        const refreshedInteraction = await fetchData<InteractionResponse>(
          api(PATH_SEGMENTS.INTERACTION, id)
        );
        setInteractionData(refreshedInteraction);
        if (refreshedInteraction.evaluation_id) {
          await refreshEvaluationData(refreshedInteraction.evaluation_id.toString());
        }
      }
    };

    refreshCompletedOutputs().catch(refreshError => {
      console.warn('Unable to refresh completed analysis output', refreshError);
    });
  }, [id, postCallJob, interactionData?.transcript_id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const loadStatus = async () => {
      try {
        const job = await getPostCallStatus(Number(id));
        if (cancelled) return;
        setPostCallJob(job);
        if (job?.status === 'queued' || job?.status === 'running') {
          timer = setTimeout(loadStatus, 2500);
        }
      } catch (statusError) {
        console.warn('Unable to load post-call status', statusError);
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  const retryPostCall = async () => {
    if (!id) return;
    setPostCallRetrying(true);
    try {
      const response = await runPostCall(Number(id), true);
      setPostCallJob(response.status);
    } catch (retryError) {
      console.error('Unable to retry post-call analysis', retryError);
    } finally {
      setPostCallRetrying(false);
    }
  };

  useEffect(() => {
    const transcriptId = transcriptData?.transcript?.id;
    if (!transcriptId) {
      return;
    }
    const fetchLiveAnalysis = async () => {
      try {
        setTurnAnalysisLoading(true);
        const url = `${api(PATH_SEGMENTS.TA, transcriptId)}?include=strict,freeflow`;
        const response = await fetchData<TurnAnalysisResponse>(url);
        setTurnAnalysisData(response);
        setTurnAnalysisError(null);
      } catch (err) {
        console.error('Failed to load turn analysis', err);
        setTurnAnalysisError('Unable to load live analysis.');
        setTurnAnalysisData(null);
      } finally {
        setTurnAnalysisLoading(false);
      }
    };
    fetchLiveAnalysis();
  }, [transcriptData?.transcript?.id]);

  // Replace the header update effect with this useLayoutEffect
  useLayoutEffect(() => {
    // Only update header props once when data is available
    if (interactionData && !headerUpdatedRef.current) {
      headerUpdatedRef.current = true;
      
      const externalId = interactionData.interaction.external_id;
      const hasValidExternalId = externalId !== undefined && externalId !== "None";
      const firstName = interactionData.interaction.agent_first_name || '';
      const lastName = interactionData.interaction.agent_last_name || '';
      const time = interactionData.interaction.time || '';
      
      // Schedule this update outside of the React render cycle
      setTimeout(() => {
        updateHeaderProps({
          pageTitle: `Interaction of ${firstName + ' ' + lastName || 'Unknown'} on ${formatDate(time)}`,
          interactionExId: `${externalId || ''}`,
          showInteractionExId: hasValidExternalId,
        });
      }, 0);
    }
  }, [interactionData, updateHeaderProps]);

  // Extract moments from transcript data
  const moments = React.useMemo(() => {
    if (!transcriptData) return [];
    
    const allMoments: { id: string; name: string; time: number; sentiment_id: number, sentiment_name: string }[] = [];
    
    transcriptData.transcript.speech_texts.forEach(speech => {
      speech.transcript_mmts?.forEach(moment => {
        allMoments.push({
          id: `moment-${moment.id}`,
          name: moment.name,
          time: speech.start_ms,
          sentiment_id: moment.sentiment_id,
          sentiment_name: moment.sentiment_name,
        });
      });
    });
    
    return allMoments;
  }, [transcriptData]);

  // Prepare transcript timestamps for the playback panel
  const transcriptTimestamps = React.useMemo(() => {
    return transcriptData?.transcript.speech_texts.map(speech => ({
      id: (speech.id || `speech-${speech.start_ms}`).toString(),
      startTime: speech.start_ms,
      endTime: speech.end_ms || speech.start_ms + 10000, // Fallback if end time is not available
      speaker:  {
        id: speech.speaker_id,
        name: speech.speaker_name,
      }
    })) || [];
  }, [transcriptData]);
  
  // Handle search with stable callback
  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
    // Update ref to track current search text
    searchStateRef.current.searchText = text;
  }, []);
  
  // Navigate to a specific match
  const navigateToMatch = useCallback((index: number) => {
    if (matchPositions.length === 0) return;
    
    // Ensure index is within bounds
    const boundedIndex = Math.max(0, Math.min(index, matchPositions.length - 1));
    
    // Update ref first to avoid re-render cycles
    searchStateRef.current.currentMatchIndex = boundedIndex;
    setCurrentMatchIndex(boundedIndex);
    
    // Get the speech element to scroll to
    const matchPosition = matchPositions[boundedIndex];
    if (matchPosition && transcriptContainerRef.current) {
      const element = document.getElementById(matchPosition.id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setSelectedTranscriptId(matchPosition.id);
      }
    }
  }, [matchPositions]);
  
  // Go to next search match
  const goToNextMatch = useCallback(() => {
    const nextIndex = (currentMatchIndex + 1) % matchPositions.length;
    navigateToMatch(nextIndex);
  }, [currentMatchIndex, matchPositions.length, navigateToMatch]);
  
  // Go to previous search match
  const goToPreviousMatch = useCallback(() => {
    const prevIndex = (currentMatchIndex - 1 + matchPositions.length) % matchPositions.length;
    navigateToMatch(prevIndex);
  }, [currentMatchIndex, matchPositions.length, navigateToMatch]);
  
  const audioRef = useRef<HTMLAudioElement>(null);


  // Handle search highlighting
  useEffect(() => {
    if (!transcriptData) return;
    
    if (searchText) {
      try {
        const searchRegex = new RegExp(searchText, 'gi');
        const highlights: string[] = [];
        const positions: Array<{id: string, index: number}> = [];
        
        transcriptData.transcript.speech_texts.forEach(speech => {
          let match;
          const speechId = (speech.id || `speech-${speech.start_ms}`).toString();
          
          while ((match = searchRegex.exec(speech.speech)) !== null) {
            highlights.push(match[0]);
            positions.push({
              id: speechId,
              index: match.index
            });
          }
        });
       
        setHighlightedTexts(highlights);
        setMatchPositions(positions);
        
        // Reset search index if needed without causing a loop
        if (positions.length > 0 && currentMatchIndex >= positions.length) {
          searchStateRef.current.currentMatchIndex = 0;
          setCurrentMatchIndex(0);
        }
      } catch (e) {
        // Handle invalid regex
        console.error("Invalid search pattern:", e);
      }
    } else {
      setHighlightedTexts([]);
      setMatchPositions([]);
      searchStateRef.current.currentMatchIndex = 0;
      setCurrentMatchIndex(0);
    }
  }, [searchText, transcriptData, currentMatchIndex]);
  
  // Handle when playback time is updated
  const handleTimeUpdate = (time: number) => {
    setCurrentTimePosition(time/1000);

    // Find the corresponding transcript segment based on time
    const segment = transcriptTimestamps.find(
      item => time >= item.startTime && time <= item.endTime
    );
    
    if (segment) {
      setSelectedTranscriptId(segment.id);

      // Switch to transcript tab if not already selected
      if (activeTab !== 'transcript') {
        setActiveTab('transcript');
        // Use setTimeout to ensure the tab switch and DOM update happens before scrolling
        setTimeout(() => {
          const speechElement = document.getElementById(`${segment.id}`);
          if (speechElement) {
            speechElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            console.log("handleTimeUpdate: speechElement not found at", time);
          }
        }, 100); // Small delay to allow tab switch to complete
      } else {
        // If already on transcript tab, scroll immediately
        const speechElement = document.getElementById(`${segment.id}`);
        if (speechElement) {
          speechElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          console.log("handleTimeUpdate: speechElement not found at", time);
        }
      }
    } else {
      console.log("handleTimeUpdate: segment not found at", time);
    }
  };
  
  // Handle when playback identifies a different segment is playing
  const handlePlayingSegmentChange = useCallback((segmentId: string) => {
    setActiveSegmentId(segmentId);
    // Auto-scroll to the segment in transcript
    const element = document.getElementById(segmentId);
    if (element && transcriptContainerRef.current) {
      const containerRect = transcriptContainerRef.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      
      if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, []);
  
  const handleTranscriptMouseEnter = (speechId: string) => {
    // const startTime = transcriptTimestamps.find(
    //   item => item.id === speechId)?.startTime;
    // if (startTime) {
    //   if (audioRef.current) {
    //     audioRef.current.currentTime = startTime/1000;
    //     setCurrentTimePosition(startTime/1000);
    //     audioRef.current.play()
    //   }
    // }
  }
  // Handle when a transcript segment is clicked
  const handleTranscriptClick = (speechId: string) => {
    setSelectedTranscriptId(speechId);

    const startTime = transcriptTimestamps.find(
      item => item.id === speechId)?.startTime;
    console.log("handleTranscriptClick: startTime", startTime);
    if (startTime) {
      console.log("handleTranscriptClick: audioRef.current.currentTime", audioRef.current?.currentTime);
      if (audioRef.current) {
        audioRef.current.currentTime = startTime/1000;
        setCurrentTimePosition(startTime/1000);
        audioRef.current.play()

      }
    }
  };

  const handleTranscriptScroll = (event: React.UIEvent<HTMLDivElement>) => {
    // console.log("handleTranscriptScroll: event", event);
  };
  
  // Update highlightText to use the ref for stable rendering
  const highlightText = useCallback((text: string, speechId: string) => {
    if (!searchText) return text;
    
    const parts = text.split(new RegExp(`(${searchText})`, 'gi'));
    let matchIndex = 0;
    
    return parts.map((part, index) => {
      if (part.toLowerCase() === searchText.toLowerCase()) {
        // Use the ref value for comparison
        const isCurrentMatch = matchPositions.length > 0 && 
          searchStateRef.current.currentMatchIndex >= 0 && 
          matchPositions[searchStateRef.current.currentMatchIndex]?.id === speechId &&
          matchPositions[searchStateRef.current.currentMatchIndex]?.index === matchIndex;
        
        matchIndex++;
        
        return (
          <mark 
            key={index} 
            className={isCurrentMatch ? 'current-match' : ''}
          >
            {part}
          </mark>
        );
      }
      
      return part;
    });
  }, [searchText, matchPositions]);

  const handleTimestampClick = (timestamp: string) => {
    // Convert timestamp string (HH:MM:SS) to milliseconds
    const [hours, minutes, seconds] = timestamp.split(':').map(Number);
    const timeInSeconds = (hours * 3600) + (minutes * 60) + seconds;
    handleTimeUpdate(timeInSeconds * 1000);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const container = document.querySelector('.interaction-main-content') as HTMLElement;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
    
    // Limit width between 30% and 80%
    const clampedWidth = Math.max(30, Math.min(80, newWidth));
    setLeftColumnWidth(clampedWidth);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add/remove global event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  // KPI loading is now handled by the useKPILoading hook
  // The hook automatically loads KPI data when the component mounts

  if (error) {
    return <div className="no-data">{error}</div>;
  }

  if (!interactionData || !transcriptData) {
    return <div className="loading">Loading interaction data...</div>;
  }

  // Calculate total duration from the transcript or fallback to interaction data
  const totalDuration = interactionData.interaction.duration * 1000 || 
    Math.max(...transcriptData.transcript.speech_texts.map(s => s.end_ms || 0));

  const analysisStatus = (name: string): PostCallStepStatus | undefined =>
    postCallJob?.steps.find(step => step.analysis_type === name)?.status;

  const analysisPanel = (
    name: string,
    label: string,
    content: React.ReactNode
  ): React.ReactNode => {
    const status = analysisStatus(name);
    if (status === 'pending' || status === 'running') {
      return (
        <div className="post-call-panel-state">
          <span className={`material-symbols-outlined ${status === 'running' ? 'spin' : ''}`}>
            {status === 'running' ? 'progress_activity' : 'schedule'}
          </span>
          <span>{status === 'running' ? `Analyzing ${label}…` : `${label} is queued`}</span>
        </div>
      );
    }
    if (status === 'failed') {
      return (
        <div className="post-call-panel-state failed">
          <span className="material-symbols-outlined">error</span>
          <span>{label} analysis failed.</span>
          <button type="button" onClick={retryPostCall} disabled={postCallRetrying}>
            Retry
          </button>
        </div>
      );
    }
    return content;
  };

  // Define the tabs
  const tabItems = [
    {
      id: 'transcript',
      label: 'Transcript',
      icon: 'subtitles',
      searchable: true
    },
    {
      id: 'moments',
      label: 'Moments',
      icon: MOMENT_ICONS.MENU,
      searchable: true,
      status: analysisStatus('mmt')
    },
    {
      id: 'KPI',
      label: 'KPI',
      icon: 'analytics',
      status: analysisStatus('kpi')
    },
    {
      id: 'summary',
      label: 'Summary',
      icon: 'summarize',
      searchable: true,
      status: analysisStatus('summary')
    },
    {
      id: 'adherence',
      label: 'Adherence',
      icon: 'checklist',
      searchable: true,
      status: analysisStatus('adherence')
    },
    {
      id: 'liveAnalysis',
      label: 'Live Analysis',
      icon: 'assistant',
      searchable: false
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: 'feedback',
      searchable: true
    },
    
  ];

  return (
    <div className="interaction-details">
      <div className="interaction-content">
        <div className="interaction-main-content">
          <div 
            className="left-column"
            style={{ width: `${leftColumnWidth}%` }}
          >
            <NavigationTabs
              tabs={tabItems}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              searchValue={searchText}
              onSearch={handleSearch}
              onNavigateNext={goToNextMatch}
              onNavigatePrevious={goToPreviousMatch}
              searchStats={
                highlightedTexts.length > 0 
                  ? {
                      currentMatch: currentMatchIndex + 1,
                      totalMatches: highlightedTexts.length
                    }
                  : undefined
              }
            />
            
            <div className="transcript-section">
              <PlaybackPanel
                audioRef={audioRef}
                audioSrc={api(PATH_SEGMENTS.AUDIO, id || "0")}
                currentTranscriptId={selectedTranscriptId || undefined}
                onTimeUpdate={handleTimeUpdate}
                onPlayingSegmentChange={handlePlayingSegmentChange}
                transcriptTimestamps={transcriptTimestamps}
                moments={moments}
                totalDuration={totalDuration}
                currentTimePosition={currentTimePosition}
                setCurrentTimePosition={setCurrentTimePosition}
              />
              
              {activeTab === 'KPI' ? (
                analysisPanel('kpi', 'KPI', <div className="kpi-panel">
                  <KPIDashboard
                    rows={[
                      {
                        id: 'score-row',
                        type: 'score',
                        kpis: [kpiCallScore, kpiAverageWpm, kpiAdherence, kpiScriptViolations].filter(Boolean)
                      },
                      {
                        id: 'trend-row',
                        type: 'trend',
                        kpis: [kpiCallScore, kpiCallScore, kpiCallScore, kpiAdherence].filter(Boolean),
                        maxItems: 3,
                        showEmptyPlaceholders: true
                      },
                      {
                        id: 'insight-row',
                        type: 'insight',
                        kpis: [kpiCallScore].filter(Boolean),
                        maxItems: 1
                      }
                    ]}
                    loading={kpiLoading}
                    error={null} // Let individual widgets handle errors gracefully
                  />
                </div>)
              ) : activeTab === 'moments' ? (
                analysisPanel('mmt', 'moments', <MomentsPanel
                  moments={moments}
                  speechTexts={transcriptData.transcript.speech_texts}
                  onSpeechClick={handleTranscriptClick}
                />)
              ) : activeTab === 'feedback' ? (
                <FeedbackBySection
                  sectionInsights={evaluationData?.sections.map(section => ({
                    section_name: section.form_section.name,
                    strengths: section.swot?.filter(item => item.swot_type_id === 1) || [],
                    weaknesses: section.swot?.filter(item => item.swot_type_id === 2) || [],
                    opportunities: section.swot?.filter(item => item.swot_type_id === 3) || []
                  })) || []}
                />
              ) : activeTab === 'summary' ? (
                analysisPanel('summary', 'summary', <InteractionSummary
                interactionId={id || ''} 
                transcriptId={transcriptData?.transcript?.id || ''} 
                />)
              ) : activeTab === 'adherence' ? (
                analysisPanel('adherence', 'adherence', <ScriptAdherence
                  interactionId={id || ''} 
                  transcriptId={transcriptData?.transcript?.id || ''} 
                  onTimestampClick={handleTimestampClick}
                />)
              ) : activeTab === 'liveAnalysis' ? (
                <LiveAnalysisTab
                  turns={turnAnalysisData?.turns || []}
                  loading={turnAnalysisLoading}
                  error={turnAnalysisError}
                  onJumpToTime={handleTimestampClick}
                />
              ) : (
                <TranscriptPanel
                  transcriptData={transcriptData}
                  activeSegmentId={activeSegmentId}
                  highlightText={highlightText}
                  onTranscriptClick={handleTranscriptClick}
                  onTranscriptMouseEnter={handleTranscriptMouseEnter}
                  onTranscriptScroll={handleTranscriptScroll}
                />
              )}
            </div>
          </div>
          
          {/* Draggable separator */}
          <div 
            className="column-separator"
            onMouseDown={handleMouseDown}
            style={{ cursor: 'col-resize' }}
          />
          
          <div 
            className="right-column"
            style={{ width: `${100 - leftColumnWidth}%` }}
          >
            {analysisStatus('eval') === 'pending' || analysisStatus('eval') === 'running' ? (
              <div className="post-call-panel-state">
                <span className={`material-symbols-outlined ${analysisStatus('eval') === 'running' ? 'spin' : ''}`}>
                  {analysisStatus('eval') === 'running' ? 'progress_activity' : 'schedule'}
                </span>
                <span>{analysisStatus('eval') === 'running' ? 'Evaluating call…' : 'Evaluation is queued'}</span>
              </div>
            ) : analysisStatus('eval') === 'failed' ? (
              <div className="post-call-panel-state failed">
                <span className="material-symbols-outlined">error</span>
                <span>Evaluation failed.</span>
                <button type="button" onClick={retryPostCall} disabled={postCallRetrying}>Retry</button>
              </div>
            ) : evaluationData ? (
              <EvaluationPanel 
                evaluation={evaluationData} 
                onTimeUpdate={handleTimeUpdate}
                onEvaluationUpdate={() => refreshEvaluationData(evaluationData.id.toString())}
              />
            ) : transcriptData?.evaluation ? (
              <EvaluationPanel 
                evaluation={transcriptData.evaluation} 
                onTimeUpdate={handleTimeUpdate}
                onEvaluationUpdate={() => refreshEvaluationData(transcriptData.evaluation?.id.toString() || '')}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractionDetails; 